import { ColDef, GridOptions } from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import get from "lodash.get";
import sortBy from "lodash.sortby";
import { ChartColumn, Eye } from "lucide-react";
import React, { memo, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import { datadogRum } from "@datadog/browser-rum";
import {
  IgIconButton,
  IgTableWrapper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  ExperienceListDto,
  HistoryDto,
  TabEnumFe,
} from "@intelligems/ig-types";
import { getExperimentType, sortByStatus } from "@intelligems/ig-utils";
import { Grid, Skeleton, useTheme } from "@mui/material";
import { useGetSummaryMetrics } from "@src/api/analytics/get-summary-metrics";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useEndExperience } from "@src/api/experience-actions/end-experience";
import { usePauseExperience } from "@src/api/experience-actions/pause-experience";
import { useStartExperience } from "@src/api/experience-actions/start-experience";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { AnalyticStatusChip } from "@src/components/chips/analyticsStatusChip";
import { DateRender } from "@src/components/DateRender";
import { AdminOverrideStartExpDialog } from "@src/components/Dialogs/AdminOverrideStartExpDialog";
import { BillingStatusDialog } from "@src/components/Dialogs/BillingStatusDialog";
import { CompletePricingIntegrationDialog } from "@src/components/Dialogs/CompletePricingIntegrationDialog";
import { ConfirmExperienceDeleteDialog } from "@src/components/Dialogs/ConfirmExperienceDeleteDialog";
import { ConfirmIgChecklist } from "@src/components/Dialogs/ConfirmIgChecklist";
import { FreeTrialDialog } from "@src/components/Dialogs/FreeTrialDialog";
import { PreviewExperience } from "@src/components/Dialogs/PreviewExperience";
import { PreviewVariationCode } from "@src/components/Dialogs/PreviewVariationCode";
import { TemplateTestGateDialog } from "@src/components/Dialogs/TemplateTestGateDialog";
import { UserInfoPopup } from "@src/components/Dialogs/UserInfoPopup";
import { ExperienceInfoDrawer } from "@src/components/Drawer/ExperienceInfoDrawer";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { ScopedComponentWithDisable } from "@src/components/LimitAccess/ScopedComponentWithDisable";
import { GroupedActionExperienceDialogs } from "@src/features/experiments/components/dialogs/GroupedActionExperienceDialogs";
import { onPriceTestAction } from "@src/features/experiments/utils/experienceActionUtils";
import { isUsedInPersonalization } from "@src/features/personalizations/utils";
import { RelatedPersonalizationActiveDialog } from "@src/features/sharedExperience/components/alertsAndWarnings/RelatedPersonalizationActiveDialog";
import { ExperimentActionButtons } from "@src/features/sharedExperience/components/ExperimentActionButtons";
import { SharedTitleColumn } from "@src/features/sharedExperience/components/sharedTableRenderers";
import { checkHasTemplateSnippet } from "@src/features/utils";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import { useDispatch, useSelector } from "@src/store";
import { selectedExperienceListFilters } from "@src/store/slices/appSettings";
import { setExperienceTabWithoutRedirect } from "@src/store/slices/experiences";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import {
  dateTimeConverter,
  fullDateTimeConverter,
} from "@src/utils/dateConverters";
import { mpClickAnalyticsButton } from "@src/utils/mixpanel";

import { MoreMenuExperiment } from "./MoreMenuExperiment";
import { StatusRenderer } from "./StatusRenderer";
import { getConfirmedIntegrationHistoryAction } from "./utils";

export const ExperimentListTable = memo(function ExperimentListTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const gridRef = useRef<AgGridReact>(null);
  const tableFilters = useSelector(selectedExperienceListFilters);
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const summaryMetrics = useGetSummaryMetrics();
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  const personalizationsList = useGetExperiencesList({
    category: "personalization",
  });
  const organization = useGetOrg();
  const scriptStatus = useGetScriptStatus(organization?.data?.org.id);
  const isAdmin = useIsAdmin();
  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  )!.value;

  const isConfirmDialogOpen = useSelector(isDialogOpen("confirmIgChecklist"));
  const [currentExperience, setCurrentExperience] = useImmer<
    ExperienceListDto | undefined
  >(undefined);

  const isSubscriptionActive = useCheckSubscriptionStatus(true);
  const hasPersonalizationPermissions =
    useConfiguration<boolean>("personalizations")?.value ?? false;

  const startExperience = useStartExperience();
  const endExperience = useEndExperience(false, currentExperience?.id);
  const pauseExperience = usePauseExperience();

  const filteredRowData = useMemo(() => {
    if (experiencesList.isSuccess) {
      let filteredData = experiencesList.data.experiencesList;

      if (tableFilters) {
        if (tableFilters.status.length > 0) {
          filteredData = experiencesList.data.experiencesList.filter(
            (experiment) => tableFilters.status.includes(experiment.status)
          );
        }
        if (tableFilters.search && tableFilters.search.length > 0) {
          const searchTerm = tableFilters.search;
          const matchesVariation = (variation: any) =>
            variation.id.includes(searchTerm);

          filteredData = filteredData.filter(
            (experiment) =>
              experiment.name
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              experiment.id === searchTerm ||
              experiment.variations.some(matchesVariation)
          );
        }
        if (tableFilters.testTypes.length > 0) {
          filteredData = filteredData.filter((experiment) =>
            tableFilters.testTypes.includes(getExperimentType(experiment))
          );
        }
      }
      return sortByStatus(filteredData);
    }
    return [];
  }, [
    experiencesList.isSuccess,
    experiencesList?.data?.experiencesList,
    tableFilters,
    currentExperience,
  ]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 100,
      resizable: false,
      menuTabs: [],
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  useEffect(() => {
    gridRef.current?.api?.redrawRows();
  }, [filteredRowData]);

  const gridOptions = useMemo<GridOptions<ExperienceListDto>>(() => {
    return {
      getRowId: (params) => params.data.id,
    };
  }, []);

  const onAdminOverrideClick = (experience: ExperienceListDto) => {
    setCurrentExperience(experience);
    if (
      checkHasTemplateSnippet(
        scriptStatus.isSuccess,
        scriptStatus.data,
        experience.type
      )
    ) {
      dispatch(setDialog({ dialog: "templateSnippetGate", open: true }));
    } else {
      dispatch(
        setDialog({
          dialog: "adminOverrideStartExp",
          open: true,
        })
      );
    }
  };

  const onExperimentEnd = (experience: ExperienceListDto) => {
    setCurrentExperience(experience);
    if (!experience.testTypes.hasTestPricing) {
      if (hasPersonalizationPermissions) {
        dispatch(setDialog({ dialog: "endNonPriceTest", open: true }));
      } else {
        // Content / Campaign Test
        endExperience.mutate({
          body: { variationId: undefined },
          queryParams: undefined,
          pathParams: {
            name: experience.name,
            experienceId: experience.id,
            action: "end",
          },
        });
      }
    } else {
      onPriceTestAction(
        experience,
        "end",
        storeCurrencyConfig?.value?.code ?? "USD",
        dispatch
      );
    }
  };

  const onExperimentStart = (experience: ExperienceListDto) => {
    setCurrentExperience(experience);
    if (experience.testTypes.hasTestPricing) {
      if (!shouldDuplicateProducts) {
        onPriceTestAction(
          experience,
          "start",
          storeCurrencyConfig?.value?.code ?? "USD",
          dispatch
        );
      } else {
        dispatch(setExperienceTabWithoutRedirect("mods"));
        void navigate(
          `${EXPERIMENT_ROUTES.Experiment}/${experience.id}` +
            `?tab=${TabEnumFe.MODS}`,
          {
            state: {
              tab: "mods",
              showDuplicateProducts: true,
              showStartTestRow: true,
            },
          }
        );
      }
    } else {
      // Template Test snippet check
      if (
        checkHasTemplateSnippet(
          scriptStatus.isSuccess,
          scriptStatus.data,
          experience.type
        )
      ) {
        dispatch(setDialog({ dialog: "templateSnippetGate", open: true }));
      } else {
        // Content/Campaign Test/Shipping
        startExperience.mutate({
          body: { variationId: undefined },
          pathParams: {
            pausedAtTs: experience.pausedAtTs,
            experienceId: experience.id,
            name: experience.name,
            action: "start",
          },
          queryParams: undefined,
        });
      }
    }
  };

  const onExperimentStartWrapper = (
    rowExperience: ExperienceListDto,
    history: HistoryDto[],
    isSubscriptionStartActive?: boolean
  ) => {
    setCurrentExperience(rowExperience);
    const isAdminOverride =
      isAdmin &&
      !isSubscriptionStartActive &&
      (rowExperience.status === "paused" || rowExperience.status === "pending");
    if (!getConfirmedIntegrationHistoryAction(history)) {
      dispatch(
        setDialog({
          dialog: "confirmIgChecklist",
          open: true,
          props: { isAdminOverride },
        })
      );
    } else if (isAdminOverride) {
      onAdminOverrideClick(rowExperience);
    } else {
      checkIfUsedInPersonalization(rowExperience);
    }
  };

  const checkIfUsedInPersonalization = (experience: ExperienceListDto) => {
    if (personalizationsList.isSuccess) {
      const isUsed = isUsedInPersonalization(
        personalizationsList.data.experiencesList,
        experience.variations || []
      );
      if (isUsed.length > 0) {
        dispatch(
          setDialog({
            dialog: "relatedPersonalizationActive",
            open: true,
            props: {
              experience: experience,
              isUsed: isUsed,
            },
          })
        );
      } else {
        onExperimentStart(experience);
      }
    }
  };

  const onExperimentPause = (experience: ExperienceListDto) => {
    setCurrentExperience(experience);
    if (experience.testTypes.hasTestPricing) {
      onPriceTestAction(
        experience,
        "pause",
        storeCurrencyConfig?.value?.code ?? "USD",
        dispatch
      );
    } else {
      pauseExperience.mutate({
        body: { variationId: undefined },
        queryParams: undefined,
        pathParams: {
          name: experience.name,
          experienceId: experience.id,
          action: "pause",
        },
      });
    }
  };

  const columnDefs: Omit<ColDef, "lockPosition">[] = useMemo(() => {
    if (experiencesList.isSuccess && filteredRowData) {
      const columnDefs: ColDef<ExperienceListDto>[] = [
        {
          headerName: "Experiment Name",
          headerClass: "header-custom",
          lockPosition: "left",
          field: "name",
          flex: 1,
          sortable: false,
          cellStyle: {
            justifyContent: "flex-start",
          },
          cellRenderer: SharedTitleColumn,
          cellRendererParams: {
            orgConfigurations: organization.data.org.configurations,
            setCurrentExperience: setCurrentExperience,
            storeCurrencyConfig: storeCurrencyConfig!.value,
          },
        },
        {
          headerName: "Visitors",
          resizable: false,
          headerClass: "header-custom2",
          sortable: false,
          flex: 0.5,
          cellStyle: {
            justifyContent: "center",
          },
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (item: CustomCellRendererProps<ExperienceListDto>) => {
            const experience = item.data!;
            if (experience.status === "pending") {
              return (
                <IgTypography color={theme.palette.text.secondary}>
                  -------
                </IgTypography>
              );
            } else {
              if (summaryMetrics.isLoading || summaryMetrics.isPending) {
                return (
                  <Skeleton
                    variant="rectangular"
                    width={20}
                    height={15}
                    sx={{ borderRadius: "5px" }}
                  />
                );
              }
              return (
                <IgTypography color={theme.palette.text.secondary}>
                  {get(
                    summaryMetrics,
                    `data[${item.data?.id || ""}].visitors`,
                    "0"
                  )}
                </IgTypography>
              );
            }
          },
        },
        {
          headerName: "Start",
          resizable: false,
          field: "startedAtTs",
          headerClass: "header-custom2",
          sortable: false,
          flex: 0.4,
          cellStyle: {
            justifyContent: "center",
          },
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (item: CustomCellRendererProps<ExperienceListDto>) => {
            const experience = item.data!;
            const experienceActions = sortBy(
              experience.experienceActions,
              (_) => _.actionTs
            );
            const firstStartAtTs = experienceActions.find(
              (action) => action.action === "start"
            )?.actionTs;
            return experience.status === "pending" ? (
              <ExperimentActionButtons
                experiencesList={experiencesList.data.experiencesList}
                organization={organization.data.org}
                renderLocation={"overview"}
                onlyStart={true}
                rowExperiment={experience}
                currentExperiment={currentExperience}
                onExperimentEnd={onExperimentEnd}
                onExperimentPause={onExperimentPause}
                onExperimentStartWrapper={onExperimentStartWrapper}
                hasSchedule={false}
                actionMode={"edit"}
              />
            ) : firstStartAtTs ? (
              <IgTooltip title={fullDateTimeConverter(firstStartAtTs)}>
                <DateRender date={dateTimeConverter(firstStartAtTs)} />
              </IgTooltip>
            ) : null;
          },
        },
        {
          headerName: "End",
          field: "endedAtTs",
          resizable: false,
          flex: 0.4,
          headerClass: "header-custom2",
          sortable: false,
          cellStyle: {
            justifyContent: "center",
          },
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (
            item: CustomCellRendererProps<ExperienceListDto, number | null>
          ) => {
            const experience = item.data!;
            if (
              experience.status === "ended" ||
              experience.status === "pending"
            ) {
              return (
                <IgTooltip
                  title={
                    experience.status === "pending"
                      ? ""
                      : fullDateTimeConverter(item.value)
                  }
                >
                  <DateRender date={dateTimeConverter(item.value)} />
                </IgTooltip>
              );
            } else {
              return (
                <ExperimentActionButtons
                  experiencesList={experiencesList.data.experiencesList}
                  organization={organization.data.org}
                  renderLocation={"overview"}
                  onlyStart={false}
                  rowExperiment={experience}
                  currentExperiment={currentExperience}
                  onExperimentEnd={onExperimentEnd}
                  onExperimentPause={onExperimentPause}
                  onExperimentStartWrapper={onExperimentStartWrapper}
                  hasSchedule={false}
                  actionMode={"edit"}
                />
              );
            }
          },
        },
        {
          headerName: "Status",
          field: "status",
          flex: 0.4,
          resizable: false,
          headerClass: "header-custom2",
          sortable: false,
          cellStyle: {
            justifyContent: "center",
          },
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (item: CustomCellRendererProps<ExperienceListDto>) => {
            const experience = item.data!;
            return (
              <StatusRenderer
                experience={experience}
                renderLocation={"overview"}
              />
            );
          },
        },
        {
          headerName: "Progress",
          resizable: false,
          headerClass: "header-custom2",
          sortable: false,
          flex: 0.45,
          cellStyle: {
            justifyContent: "center",
          },
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (item: CustomCellRendererProps<ExperienceListDto>) => {
            const experience = item.data!;
            return <AnalyticStatusChip experience={experience} />;
          },
        },
        {
          headerName: "",
          sortable: false,
          headerClass: "header-custom2",
          resizable: false,
          flex: 0.1,
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (item: CustomCellRendererProps<ExperienceListDto>) => {
            const experience = item.data!;
            if (experience.status === "pending") {
              return (
                <IgTooltip title="Preview Experiment">
                  <IgIconButton
                    color={"primary"}
                    onClick={() => {
                      setCurrentExperience(experience);
                      if (!scriptStatus.data?.exists) {
                        dispatch(
                          setDialog({
                            dialog: "redirectToPluginDialog",
                            open: true,
                            props: {
                              experience: item.data,
                              id: experience.id,
                              path:
                                experience.previewPath ||
                                experience.devicePreview?.path,
                            },
                          })
                        );
                      } else {
                        dispatch(
                          setDialog({
                            dialog: "previewExperiment",
                            open: true,
                            props: {
                              id: experience.id,
                              path:
                                experience.previewPath ||
                                experience.devicePreview?.path,
                            },
                          })
                        );
                      }
                    }}
                  >
                    <Eye size={20} />
                  </IgIconButton>
                </IgTooltip>
              );
            } else {
              return (
                <ScopedComponentWithDisable
                  isEnabled={isSubscriptionActive}
                  debugId={"view-analytics"}
                  customDisabledTooltipMessage={
                    "Please upgrade your plan to view analytics."
                  }
                  customEnabledTooltipMessage={"View Analytics"}
                >
                  <IgIconButton
                    color={"primary"}
                    onClick={() => {
                      if (!isSubscriptionActive && !isAdmin) return;
                      mpClickAnalyticsButton(
                        experience.name,
                        experience.id,
                        "Analytics chart icon"
                      );
                      void navigate(
                        `${EXPERIMENT_ROUTES.Experiment}/${experience.id}?action=edit&type=${experience.type}&tab=${TabEnumFe.ANALYTICS}`,
                        { state: { name: "overview" } }
                      );
                    }}
                    disabled={false}
                  >
                    <ChartColumn size={20} />
                  </IgIconButton>
                </ScopedComponentWithDisable>
              );
            }
          },
        },
        {
          headerName: "",
          headerClass: "header-custom2",
          resizable: false,
          sortable: false,
          flex: 0.1,
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (item: CustomCellRendererProps<ExperienceListDto>) => {
            const experience = item.data!;
            return (
              <MoreMenuExperiment
                scriptStatus={scriptStatus.data || null}
                organization={organization.data.org}
                experience={experience}
                setCurrExperience={setCurrentExperience}
                renderLocation={"overview"}
              />
            );
          },
        },
      ];
      return columnDefs;
    } else return [];
  }, [
    experiencesList?.data?.experiencesList,
    filteredRowData,
    currentExperience,
    experiencesList.isSuccess,
    scriptStatus,
    organization?.data?.org?.configurations,
    onExperimentPause,
    onExperimentEnd,
    onExperimentStart,
    onExperimentStartWrapper,
  ]);

  useEffect(() => {
    if (experiencesList.isLoading) {
      gridRef.current?.api?.setGridOption("loading", true);
    } else if (filteredRowData.length === 0 && experiencesList.isSuccess) {
      gridRef.current?.api?.showNoRowsOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, [experiencesList.isLoading, filteredRowData, experiencesList.isSuccess]);

  useEffect(() => {
    if (filteredRowData?.length) {
      datadogRum.addTiming("experiencesListLoaded");
    }
  }, [filteredRowData]);

  if (filteredRowData) {
    return (
      <Grid container direction={"column"} rowSpacing={3}>
        {/*<UserInfoPopup />*/}
        <TemplateTestGateDialog organization={organization.data.org} />
        {currentExperience && (
          <>
            <ConfirmExperienceDeleteDialog
              currentExperience={currentExperience}
              redirect={false}
            />
            <ExperienceInfoDrawer />

            <GroupedActionExperienceDialogs
              experience={currentExperience}
              variations={currentExperience.variations}
              isExperienceRender={false}
            />
            <RelatedPersonalizationActiveDialog
              handleStartExperiment={(experience) =>
                onExperimentStart(experience)
              }
            />
            <PreviewExperience
              experience={currentExperience}
              organization={organization.data.org}
              redirects={currentExperience.variations
                .map((variation) => variation.redirects || [])
                .flat()}
              pageTargeting={currentExperience.experiencePageTargeting}
            />
            <AdminOverrideStartExpDialog
              experience={currentExperience}
              onExpButtonClick={checkIfUsedInPersonalization}
            />
            <RelatedPersonalizationActiveDialog
              handleStartExperiment={(experience) =>
                onExperimentStart(experience)
              }
            />
            {isConfirmDialogOpen && (
              <ConfirmIgChecklist
                onExperienceStart={checkIfUsedInPersonalization}
                experience={currentExperience}
                onAdminOverrideClick={onAdminOverrideClick}
              />
            )}
            <PreviewVariationCode
              experience={currentExperience}
              variations={currentExperience.variations}
              organization={organization.data.org}
              redirects={currentExperience.variations
                .map((variation) => variation.redirects || [])
                .flat()}
              pageTargeting={currentExperience.experiencePageTargeting}
            />
          </>
        )}
        <BillingStatusDialog />
        <CompletePricingIntegrationDialog />
        <FreeTrialDialog />
        <Grid item>
          <IgTableWrapper
            minHeight={null}
            sx={{
              height: "468px",
            }}
          >
            <AgGridReact
              ref={gridRef}
              suppressContextMenu={true}
              suppressCsvExport={true}
              columnDefs={columnDefs}
              rowData={filteredRowData}
              suppressAggFuncInHeader={true}
              suppressDragLeaveHidesColumns={true}
              suppressCellFocus={true}
              suppressRowHoverHighlight={true}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              loading={experiencesList.isLoading}
              overlayLoadingTemplate={"<div>Loading Experiment Data ... </div>"}
              overlayNoRowsTemplate={"<div>No Experiments Found</div>"}
            />
          </IgTableWrapper>
        </Grid>
      </Grid>
    );
  }
  return null;
});
